/**
 * @file 画面下に固定する保存バー。未保存の変更があるときだけ出て、保存後は数秒だけ結果を知らせる。
 *
 * @what: dirty / saving / saveError / savedAt を受けて「未保存の変更があります［保存する］」
 *   「保存中...」「保存しました」「保存できませんでした」を出し分ける。
 * @why: 状態ボタン・いまここ・メモのどこを触っても保存の導線が同じ場所にあるようにする
 *   (パネル内のボタンだと操作箇所から遠く、見落とす)。
 * @failure: 保存失敗時は文言のみ表示し、ボタンを残して再試行できるようにする (内部エラーは出さない)。
 *   二重送信は saving 中の disabled で防ぐ。
 */

import { useEffect, useState } from "react";
import "./study.css";

const SAVED_MESSAGE_MS = 3000;

export function SaveBar({
  dirty,
  saving,
  saveError,
  savedAt,
  onSave,
}: {
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  savedAt: string | null;
  onSave: () => void;
}) {
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (!savedAt) {
      setShowSaved(false);
      return;
    }
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), SAVED_MESSAGE_MS);
    return () => clearTimeout(timer);
  }, [savedAt]);

  const pending = dirty || saving;
  if (!pending && !saveError && !showSaved) return null;

  return (
    <div className="study-save-bar" role="region" aria-label="保存">
      <div className="study-save-bar-inner">
        {saveError ? (
          <span role="alert" className="study-save-error">
            {saveError}
          </span>
        ) : pending ? (
          <span className="study-save-status">未保存の変更があります</span>
        ) : (
          <span role="status" className="study-save-status">
            保存しました
          </span>
        )}
        {pending || saveError ? (
          <button type="button" className="study-save-btn" onClick={onSave} disabled={saving}>
            {saving ? "保存中..." : "保存する"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
